export interface MetricCoordinate {
  x: number;
  y: number;
}

const SEMI_MAJOR_AXIS = 6378137;
const FLATTENING = 1 / 298.257222101;
const SCALE_FACTOR = 0.9996;
const CENTRAL_MERIDIAN_RADIANS = (-57 * Math.PI) / 180;
const ECCENTRICITY_SQUARED = FLATTENING * (2 - FLATTENING);
const SECOND_ECCENTRICITY_SQUARED =
  ECCENTRICITY_SQUARED / (1 - ECCENTRICITY_SQUARED);

// EPSG:31981 (SIRGAS 2000 / UTM zone 21S), matching the Python renderer.
export function projectToUtm21S(lat: number, lng: number): MetricCoordinate {
  const latitude = (lat * Math.PI) / 180;
  const longitude = (lng * Math.PI) / 180;
  const sinLatitude = Math.sin(latitude);
  const cosLatitude = Math.cos(latitude);
  const tanLatitude = Math.tan(latitude);
  const eccentricityFourth = ECCENTRICITY_SQUARED * ECCENTRICITY_SQUARED;
  const eccentricitySixth = eccentricityFourth * ECCENTRICITY_SQUARED;
  const radius =
    SEMI_MAJOR_AXIS /
    Math.sqrt(1 - ECCENTRICITY_SQUARED * sinLatitude * sinLatitude);
  const tangentSquared = tanLatitude * tanLatitude;
  const secondEccentricityTerm =
    SECOND_ECCENTRICITY_SQUARED * cosLatitude * cosLatitude;
  const longitudeTerm = cosLatitude * (longitude - CENTRAL_MERIDIAN_RADIANS);
  const meridionalArc =
    SEMI_MAJOR_AXIS *
    ((1 -
      ECCENTRICITY_SQUARED / 4 -
      (3 * eccentricityFourth) / 64 -
      (5 * eccentricitySixth) / 256) *
      latitude -
      ((3 * ECCENTRICITY_SQUARED) / 8 +
        (3 * eccentricityFourth) / 32 +
        (45 * eccentricitySixth) / 1024) *
        Math.sin(2 * latitude) +
      ((15 * eccentricityFourth) / 256 + (45 * eccentricitySixth) / 1024) *
        Math.sin(4 * latitude) -
      ((35 * eccentricitySixth) / 3072) * Math.sin(6 * latitude));

  const x =
    SCALE_FACTOR *
      radius *
      (longitudeTerm +
        ((1 - tangentSquared + secondEccentricityTerm) *
          Math.pow(longitudeTerm, 3)) /
          6 +
        ((5 -
          18 * tangentSquared +
          tangentSquared * tangentSquared +
          72 * secondEccentricityTerm -
          58 * SECOND_ECCENTRICITY_SQUARED) *
          Math.pow(longitudeTerm, 5)) /
          120) +
    500000;
  let y =
    SCALE_FACTOR *
    (meridionalArc +
      radius *
        tanLatitude *
        (Math.pow(longitudeTerm, 2) / 2 +
          ((5 -
            tangentSquared +
            9 * secondEccentricityTerm +
            4 * secondEccentricityTerm * secondEccentricityTerm) *
            Math.pow(longitudeTerm, 4)) /
            24 +
          ((61 -
            58 * tangentSquared +
            tangentSquared * tangentSquared +
            600 * secondEccentricityTerm -
            330 * SECOND_ECCENTRICITY_SQUARED) *
            Math.pow(longitudeTerm, 6)) /
            720));
  if (lat < 0) {
    y += 10000000;
  }
  return { x, y };
}
